(() => {
  if (customElements.get('prestige-hero-video')) return;

  class PrestigeHeroVideo extends HTMLElement {
    static get observedAttributes() {
      return ['data-autoplay', 'data-desktop-src', 'data-mobile-src', 'data-desktop-poster', 'data-mobile-poster'];
    }

    connectedCallback() {
      if (this.lifecycle) return;
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-video-toggle]');
      if (!this.video || !this.button) return;

      this.lifecycle = new AbortController();
      const lifecycle = this.lifecycle;
      const options = { signal: lifecycle.signal };
      const current = () => this.lifecycle === lifecycle && this.isConnected;
      this.mobile = matchMedia('(max-width: 699px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.userPaused = false;
      this.manualPlay = false;
      this.autoplayBlocked = false;
      this.failed = false;
      this.desiredSource = '';
      this.currentSource = '';
      this.pendingPlay = null;
      this.dataset.ready = 'false';
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;

      this.button.addEventListener('click', () => {
        if (!this.video.paused || this.pendingPlay) {
          this.userPaused = true;
          this.manualPlay = false;
          this.pause();
        } else {
          this.userPaused = false;
          this.manualPlay = true;
          this.autoplayBlocked = false;
          this.update();
        }
        this.renderButton();
      }, options);

      this.video.addEventListener('loadeddata', () => {
        if (this.failed || this.video.readyState < 2 || !this.currentSource || this.currentSource !== this.desiredSource) return;
        if (this.video.currentSrc && this.video.currentSrc !== this.currentSource) return;
        this.dataset.ready = 'true';
        this.renderButton();
      }, options);
      this.video.addEventListener('play', () => {
        if (!this.shouldPlay()) this.pause();
        this.renderButton();
      }, options);
      this.video.addEventListener('pause', () => this.renderButton(), options);
      this.video.addEventListener('ended', () => {
        this.userPaused = true;
        this.manualPlay = false;
        this.renderButton();
      }, options);
      this.video.addEventListener('error', () => {
        if (!this.currentSource || (this.video.currentSrc && this.video.currentSrc !== this.currentSource)) return;
        this.failed = true;
        this.dataset.ready = 'false';
        this.pause();
        this.renderButton();
      }, options);

      this.mobile.addEventListener('change', () => this.update(), options);
      this.motion.addEventListener('change', () => {
        // A newly enabled reduced-motion preference stops even manually started media.
        this.manualPlay = false;
        this.update();
      }, options);
      document.addEventListener('visibilitychange', () => this.update(), options);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (!current()) return;
          this.visible = entries.some(entry => entry.target === this && entry.isIntersecting);
          this.update();
        }, { threshold: 0.01 });
        this.observer.observe(this);
      } else {
        const checkVisibility = () => {
          if (!current()) return;
          const rect = this.getBoundingClientRect();
          this.visible = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
          this.update();
        };
        window.addEventListener('scroll', checkVisibility, { ...options, passive: true });
        window.addEventListener('resize', checkVisibility, options);
        checkVisibility();
      }
      this.update();
    }

    attributeChangedCallback() {
      if (this.lifecycle) this.update();
    }

    selectSource() {
      const rawSource = this.mobile.matches
        ? (this.dataset.mobileSrc || this.dataset.desktopSrc || '')
        : (this.dataset.desktopSrc || this.dataset.mobileSrc || '');
      let source = '';
      if (rawSource.trim()) {
        try {
          const url = new URL(rawSource, document.baseURI);
          if (url.protocol === 'https:' || url.protocol === 'http:') source = url.href;
        } catch {
          // Invalid source settings keep the existing image fallback visible.
        }
      }
      const poster = this.mobile.matches
        ? (this.dataset.mobilePoster || this.dataset.desktopPoster || '')
        : (this.dataset.desktopPoster || this.dataset.mobilePoster || '');
      if (poster) this.video.setAttribute('poster', poster);
      else this.video.removeAttribute('poster');

      if (source === this.desiredSource) return;
      this.desiredSource = source;
      this.currentSource = '';
      this.pendingPlay = null;
      this.failed = false;
      this.autoplayBlocked = false;
      this.dataset.ready = 'false';
      this.pause();
      if (this.video.hasAttribute('src')) {
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    shouldPlay() {
      const autoplay = this.dataset.autoplay === 'true' && !this.motion.matches && !this.autoplayBlocked;
      return Boolean(this.lifecycle && this.isConnected && this.visible && !document.hidden && !this.failed && !this.userPaused && (this.manualPlay || autoplay));
    }

    update() {
      if (!this.lifecycle || !this.isConnected) return;
      this.selectSource();
      if (this.desiredSource && this.visible && !document.hidden && !this.failed && this.currentSource !== this.desiredSource) {
        this.currentSource = this.desiredSource;
        this.video.src = this.currentSource;
        this.video.load();
      }
      if (this.currentSource && this.shouldPlay()) {
        if (this.video.paused && !this.pendingPlay) this.play();
        else if (this.video.paused && this.pendingPlay) this.pendingPlay.needsUpdate = true;
      } else {
        this.pause();
      }
      this.renderButton();
    }

    play() {
      const attempt = { lifecycle: this.lifecycle, source: this.currentSource };
      this.pendingPlay = attempt;
      this.video.muted = true;
      let promise;
      try {
        promise = this.video.play();
      } catch (error) {
        promise = Promise.reject(error);
      }
      Promise.resolve(promise).then(() => {
        if (this.lifecycle !== attempt.lifecycle || this.pendingPlay !== attempt) return;
        if (!this.shouldPlay()) this.pause();
      }).catch(error => {
        if (this.lifecycle !== attempt.lifecycle || this.pendingPlay !== attempt) return;
        attempt.aborted = error?.name === 'AbortError';
        if (!attempt.aborted) {
          // Do not retry rejected autoplay on each observer/visibility event.
          // The visible button still permits a direct user gesture to start it.
          this.autoplayBlocked = true;
          this.manualPlay = false;
        }
      }).finally(() => {
        if (this.lifecycle !== attempt.lifecycle || this.pendingPlay !== attempt) return;
        this.pendingPlay = null;
        this.renderButton();
        // A rapid offscreen/on-screen transition can abort the old play request.
        // Retry only when a new update arrived while it was pending, not in a loop.
        if (attempt.aborted && attempt.needsUpdate) this.update();
      });
    }

    pause() {
      if (this.video && !this.video.paused) this.video.pause();
    }

    renderButton() {
      this.button.hidden = !this.desiredSource || this.failed;
      const paused = this.video.paused;
      const playLabel = this.button.querySelector('[data-play-label]');
      const pauseLabel = this.button.querySelector('[data-pause-label]');
      if (playLabel) playLabel.hidden = !paused;
      if (pauseLabel) pauseLabel.hidden = paused;
      const label = (paused ? playLabel : pauseLabel)?.textContent?.trim();
      if (label) this.button.setAttribute('aria-label', label);
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.observer?.disconnect();
      this.observer = null;
      this.pendingPlay = null;
      this.pause();
      if (this.video?.hasAttribute('src')) {
        this.video.removeAttribute('src');
        this.video.load();
      }
      this.dataset.ready = 'false';
    }
  }

  customElements.define('prestige-hero-video', PrestigeHeroVideo);
})();
